import * as React from 'react';
import Box from '@mui/material/Box';
import {
    DataGridPro,
    GridColDef,
    GridColumnHeaderParams,
    GridRenderCellParams,
    GridEventListener,
    GridRowEditStopParams,
    GridSelectionModel,
    GridRowParams,
    useGridApiRef,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    //GridCallbackDetails,
} from '@mui/x-data-grid-pro';
import { Pagination } from 'src/types/common';
import { Backdrop, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { Account } from '../../../../stores/AccountStore';
import OptionsLoader from '../../../../features/common/OptionsLoader';
import SimpleDialog from '../../../../features/common/SimpleDialog';
import CloseButton from '../../../components/Button/CloseButton';
import RenderIf, { ClaimType } from '../../../../features/common/RenderIf';
import SectionAppBar from '../../../components/Section/SectionAppBar';
import { ApiResponseType } from '../../../../types';
import AccountImportStore from './store/AccountImportStore';
import { Filter, FilterType } from '../../../../features/filters/FilterTypes';
import { client } from '../../../../utils/axios-client';
import ApplicationStore from '../../../../stores/ApplicationStore';
import StatusColumn from './column-components/StatusColumn';
import ValidationColumn from './column-components/ValidationColumn';
import TextColumn from './column-components/TextColumn';
import CountryColumn from './column-components/CountryColumn';
import IndustryColumn from './column-components/IndustryColumn';
import RegionColumn from './column-components/RegionColumn';
import { GridCallbackDetails } from '@mui/x-data-grid';
import FallbackSpinner from '../../../../materio/@core/components/spinner';

const columns: GridColDef[] = [
    {
        field: 'duplicates',
        headerName: 'Duplicate',
        width: 100,
        editable: false,

        renderCell: (params: GridRenderCellParams<Account>) => <StatusColumn account={params.row} />,
    },
    {
        field: 'validation',
        headerName: 'Valid?',
        width: 80,
        editable: false,

        renderCell: (params: GridRenderCellParams<Account>) => <ValidationColumn account={params.row} />,
    },
    {
        field: 'name',
        width: 200,
        editable: true,
        renderHeader: (params: GridColumnHeaderParams) => <strong>{'Account Name'}</strong>,
        renderCell: (params: GridRenderCellParams<Account>) => (
            <TextColumn value={params.row.name} error={params.row.nameError} />
        ),
    },
    {
        field: 'hqCountryId',
        headerName: 'HQ Country Name',
        width: 350,
        editable: true,
        valueGetter: (params) => params.row.hqCountryId,
        //valueSetter: (params: GridValueSetterParams) => {
        //    params.value
        //    return {
        //        ...params.row, countryId: params.value
        //    }
        //},
        renderEditCell: (params: GridRenderCellParams<Account>) => (
            <CountryColumn value={params.row.hqCountryId} id={params.row.id} />
        ),
        renderCell: (params: GridRenderCellParams<Account>) => (
            <TextColumn value={params.row.hqCountryName} error={false} />
        ),
    },
    {
        field: 'industryId',
        headerName: 'Industry',
        width: 350,
        editable: true,
        valueGetter: (params) => params.row.industryId,
        //valueSetter: (params: GridValueSetterParams) => {
        //    params.value
        //    return {
        //        ...params.row, AccountIndustryId: params.value
        //    }
        //},
        renderEditCell: (params: GridRenderCellParams<Account>) => (
            <IndustryColumn value={params.row.industryId} id={params.row.id} />
        ),
        renderCell: (params: GridRenderCellParams<Account>) => (
            <TextColumn value={params.row.industryName} error={false} />
        ),
    },
    {
        field: 'hqRegionId',
        headerName: 'Region',
        width: 350,
        editable: true,
        valueGetter: (params) => params.row.hqRegionId,
        //valueSetter: (params: GridValueSetterParams) => {
        //    params.value
        //    return {
        //        ...params.row, AccountRegionId: params.value
        //    }
        //},
        renderEditCell: (params: GridRenderCellParams<Account>) => (
            <RegionColumn value={params.row.hqRegionId} id={params.row.id} />
        ),
        renderCell: (params: GridRenderCellParams<Account>) => (
            <TextColumn value={params.row.hqRegionName} error={false} />
        ),
    },
];

type Props = {
    onClose: () => void;
};

const AccountDataGrid = ({ onClose }: Props) => {
    const apiRef = useGridApiRef();
    const rows = AccountImportStore.useState((s) => s.accounts);
    const [numberOfRows, setNumberOfRows] = React.useState(10000);
    const [importColumns, setImportColumns] = React.useState([] as string[]);
    const [AccountImportDialogOpen, setAccountImportDialogOpen] = React.useState(false);
    const [AccountImporting, setAccountImporting] = React.useState(false);
    const [selectedAccountIds, setSelectedAccountIds] = React.useState<string[]>([]);
    const [missingColumns, setMissingColumns] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(false);

    const [filter, setFilter] = React.useState({
        id: crypto.randomUUID(),
        description: '',
        searchTerm: '',
        options: [],
        activityDate: 'today',
        filterType: FilterType.User,
    } as Filter);

    React.useEffect(() => {
        setImportColumns(
            columns.filter((s) => s.field !== 'duplicates' && s.field !== 'validation').map((s) => s.field),
        );
    }, [filter]);

    const onFiltersChanged = (newFilter: Filter) => {
        setFilter(newFilter);
    };

    const processRowUpdate = async (newRow: Account, oldRow: Account) => {
        return newRow;
    };

    const onSelectionModeChange = async (ids: GridSelectionModel, details: GridCallbackDetails) => {
        const selectedIds = ids.map((s) => s.toString());
        setSelectedAccountIds(selectedIds);
    };

    const onImportAccounts = async () => {
        setAccountImporting(true);
        setAccountImportDialogOpen(true);

        const accountsToImport = structuredClone(rows.filter((s) => selectedAccountIds.indexOf(s.id) !== -1));

        try {
            const response = await client.post<ApiResponseType>('/api/accounts/import', {
                accounts: accountsToImport,
            });

            if (response.data.succeeded) {
                let AccountsImported = 0;

                AccountImportStore.update((s, o) => {
                    const tempRows = structuredClone(o.accounts.filter((s) => selectedAccountIds.indexOf(s.id) === -1));

                    AccountsImported = o.accounts.length - tempRows.length;

                    s.accounts = tempRows;
                });

                setSelectedAccountIds([]);

                ApplicationStore.update((s) => {
                    s.snackbarMessage = `${AccountsImported} Accounts imported into Nostra`;
                    s.snackbarOpen = true;
                });
            } else {
                alert('error occured');
            }
        } catch {
            alert('error occured while importing accounts');
        } finally {
            setAccountImporting(false);
            setAccountImportDialogOpen(false);
        }
    };

    //const onAccountImportDialogClose = async (cancelled: boolean) => {
    //    if (AccountImporting && !cancelled) {
    //        const response = await client.post<ApiResponseType>('/api/accounts/import', {
    //            accounts: selectedAccountIds,
    //        });

    //        if (response.data.succeeded) {
    //            let AccountsImported = 0;

    //            AccountImportStore.update((s, o) => {
    //                const tempRows = structuredClone(o.accounts.filter((s) => selectedAccountIds.indexOf(s.id) === -1));

    //                AccountsImported = o.accounts.length - tempRows.length;

    //                s.accounts = tempRows;
    //            });

    //            setSelectedAccountIds([]);

    //            ApplicationStore.update((s) => {
    //                s.snackbarMessage = `${AccountsImported} Accounts imported into Nostra`;
    //                s.snackbarOpen = true;
    //            });
    //        } else {
    //            alert('error occured');
    //        }
    //    }

    //    setAccountImportDialogOpen(false);
    //    setAccountImporting(false);
    //};

    const onAddRow = () => {
        AccountImportStore.update((s, o) => {
            const temp = structuredClone(o.accounts);

            temp.unshift({
                id: crypto.randomUUID(),
                name: '',
            } as Account);

            s.accounts = temp;
        });
    };

    const onProcessRowUpdateError = (error: any) => {
        console.log('error', error);
    };

    return (
        <>
            <OptionsLoader>
                <Box sx={{ width: '100%', height: '90%' }}>
                    <SectionAppBar
                        title={`Account Import`}
                        subTitle={''}
                        buttons={
                            <>
                                <Button size="small" variant="contained" onClick={onAddRow} sx={{ mr: 2 }}>
                                    Add Row
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={onImportAccounts}
                                    disabled={selectedAccountIds.length === 0}
                                >
                                    Import Selected
                                </Button>
                                <CloseButton color="secondary" onClick={onClose}>
                                    Close
                                </CloseButton>
                            </>
                        }
                    />

                    <Grid container justifyContent="center" alignItems="center">
                        <Grid item xs={12} md={12}>
                            {/*<AccountSearch onChange={onFiltersChanged} />*/}
                        </Grid>
                    </Grid>

                    <DataGridPro
                        apiRef={apiRef}
                        rows={rows}
                        columns={columns}
                        pageSize={numberOfRows}
                        rowsPerPageOptions={[numberOfRows]}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={onProcessRowUpdateError}
                        checkboxSelection
                        disableSelectionOnClick
                        onSelectionModelChange={onSelectionModeChange}
                        isRowSelectable={(params: GridRowParams) => params.row.isValid}
                        experimentalFeatures={{ newEditingApi: true }}
                        pinnedColumns={{
                            left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'duplicates', 'validation', 'name'],
                        }}
                    />
                </Box>
                <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            </OptionsLoader>
            {AccountImportDialogOpen && (
                <SimpleDialog
                    open={AccountImportDialogOpen}
                    onClose={() => setAccountImportDialogOpen(false)}
                    fullHeight={false}
                    size="sm"
                >
                    <Box display="flex" textAlign="center" flexDirection="column">
                        <Typography variant="h6">Importing Accounts</Typography>
                        <FallbackSpinner sx={{ maxHeight: 300 }} />
                        {/*<AccountImportDialog importing={AccountImportDialogOpen} onClose={onAccountImportDialogClose} />*/}
                    </Box>
                </SimpleDialog>
            )}
        </>
    );
};

export { AccountDataGrid };
export default AccountDataGrid;
